// "Did the reviewer change anything on this draft?" — asked by דלג on the
// review screen (#260): with no changes it moves on at once; with changes it
// offers to save them first or throw them away.
//
// Compared against the snapshot the form OPENED with (startEdit), and only on
// what a person types. The derived fields — subtotal, total, a line's
// line_total/credit_total, VAT while it is computed — are recalculated by the
// page's own effects right after opening, so comparing them would call every
// draft "changed" before anyone touched it. VAT counts only when it is typed
// by hand (compareVat): in auto mode it follows the lines, which count.

const FORM_TEXT = ['supplier_id', 'doc_type', 'doc_num', 'date', 'status', 'invoice_discount_type'];
const FORM_NUM = ['invoice_discount', 'is_price_diff_credit'];
const ITEM_TEXT = ['sku', 'description', 'discount_type'];
const ITEM_NUM = ['qty', 'unit_price', 'discount', 'credit_qty', 'credit_price_diff', 'deposit', 'purchase_tax'];

const text = (v) => String(v ?? '').trim();
const num = (v) => parseFloat(v) || 0;
// A typed number is compared as typed: prices keep three decimals, and 0.125 →
// 0.129 on 1,000 units moves the line by ₪4. The margin only absorbs binary
// noise. A hand-kept VAT is stored money, so it is compared at the agora: the
// opening may round a legacy figure, and that is nobody's edit.
const sameTyped = (a, b) => Math.abs(num(a) - num(b)) < 1e-6;
const sameMoney = (a, b) => Math.abs(num(a) - num(b)) < 0.005;

const changedIn = (a, b, textKeys, numKeys) =>
  textKeys.some(k => text(a?.[k]) !== text(b?.[k])) ||
  numKeys.some(k => !sameTyped(a?.[k], b?.[k]));

export function hasUnsavedEdits(baseline, current, { compareVat = false } = {}) {
  if (!baseline) return false;
  const baseForm = baseline.form || {};
  const curForm = current?.form || {};
  if (changedIn(baseForm, curForm, FORM_TEXT, FORM_NUM)) return true;
  if (compareVat && !sameMoney(baseForm.vat, curForm.vat)) return true;
  const baseItems = baseline.items || [];
  const curItems = current?.items || [];
  if (baseItems.length !== curItems.length) return true;
  return baseItems.some((it, i) => changedIn(it, curItems[i], ITEM_TEXT, ITEM_NUM));
}

// What leaving the document page would drop, as the question to ask first
// (#369) — null when nothing would be lost. One dialog, never two in a row:
// unsaved edits lead and a waiting customer note joins them as one sentence,
// because a discard the reviewer has to confirm twice gets clicked through.
export function leavePrompt({ changed = false, pendingNote = false } = {}) {
  if (changed) {
    return {
      title: 'לצאת בלי לשמור?',
      message: `השינויים במסמך לא נשמרו ויאבדו.${pendingNote ? ' גם ההערה שממתינה ללקוח לא תישלח.' : ''}`,
      confirmLabel: 'צא בלי לשמור',
      // Not the default ביטול: on the review bar right behind this dialog,
      // ביטול is the discard itself.
      cancelLabel: 'חזרה לעריכה',
      tone: 'danger',
    };
  }
  if (pendingNote) {
    return {
      title: 'הערה ממתינה ללקוח',
      message: 'יש הערה שממתינה לשליחה ללקוח אחרי אישור המסמך. לצאת בלי לשלוח אותה?',
      confirmLabel: 'צא בלי לשלוח',
    };
  }
  return null;
}
